import React, { useEffect, useCallback } from 'react';
import { ChatProvider, useChat } from './ChatContext';
import ChatTriggerButton from './ChatTriggerButton';
import ChatPanel from './ChatPanel';

/**
 * Internal Chat Feature Component
 * Uses the Chat context for state management
 */
const ChatFeatureInternal = ({ currentDataContext, className }) => {
  const { 
    isOpen, 
    togglePanel, 
    closePanel, 
    hasUnreadMessages,
    updateContext 
  } = useChat();

  // Update context when prop changes
  useEffect(() => {
    if (currentDataContext) {
      updateContext(currentDataContext);
    }
  }, [currentDataContext, updateContext]);

  // Handle keyboard shortcut (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        togglePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePanel]);

  return (
    <div className={`chat-feature ${className || ''}`}>
      <ChatTriggerButton 
        isOpen={isOpen}
        onClick={togglePanel}
        hasUnreadMessages={hasUnreadMessages}
      />
      <ChatPanel 
        isOpen={isOpen}
        onClose={closePanel}
      />
    </div>
  );
};

/**
 * Chat Feature Component
 * Main entry point for the chat functionality
 * 
 * @param {Object} props
 * @param {Object} [props.currentDataContext] - Current data view context
 * @param {string} [props.initialConversationId] - Optional: Resume existing conversation
 * @param {Array} [props.initialMessages] - Optional: Pre-populate messages
 * @param {string} [props.className] - Optional: Additional CSS class
 */
const ChatFeature = ({ 
  currentDataContext,
  initialConversationId,
  initialMessages,
  className
}) => {
  return (
    <ChatProvider initialContext={currentDataContext}>
      <ChatFeatureInternal 
        currentDataContext={currentDataContext}
        className={className}
      />
    </ChatProvider>
  );
};

export default ChatFeature;

// Also export individual components for flexibility
export { ChatProvider, useChat } from './ChatContext';
export { default as ChatTriggerButton } from './ChatTriggerButton';
export { default as ChatPanel } from './ChatPanel';
export { default as ChatHeader } from './ChatHeader';
export { default as ChatMessages } from './ChatMessages';
export { default as ChatInput } from './ChatInput';
export { default as MessageBubble } from './MessageBubble';
export { default as ContextBanner } from './ContextBanner';
export { default as TypingIndicator } from './TypingIndicator';
